import React, { useState, useEffect } from 'react'
import { IonContent, IonFooter, IonHeader, IonModal } from '@ionic/react'
import {
  memberMobx,
  payMobx,
  payPlanMobx,
  virtualOrderMobx,
  payChannelMobx,
  bankCardMobx,
  storeMobx,
  payRechargeMobx
} from '@mobxStore'
import { cartMobx, orderMobx, discountMobx } from 'restaurant'
import { filter, pick, map, sortBy } from '@dt/fe-utils/dist/lodash'
import { format, toDecimal, trace, delayTime } from '@dt/fe-utils'
import { loadingLayer, getPlatform, toastLayer, modalLayer } from '@utils'
import { saveOrder } from '@common/utils/pay'
import { observer } from 'mobx-react'
import BankCardSimpleItem from '@pages/checkout/component/Pay/BankCardOnline/SimpleItem'
import WechatSimpleItem from '@pages/checkout/component/Pay/WechatPay/SimpleItem'
import AlipaySimpleItem from '@pages/checkout/component/Pay/Alipay/SimpleItem'
import ApplePayMethodItem from '@pages/checkout/component/Pay/ApplePayWeb/MethodItem'
import CheckoutPayController from '@ctrls/checkoutPayController'
import CashierController from '@ctrls/cashierController'
import RestoreOrderController from '@ctrls/restoreOrderController'
import timePickMobx from '@mobx/cashier/timePick.mobx'
import './index.scss'

interface ICartProps {
  reject?: () => void;
}

let isPayOrdering = false

const PayAndRecharge: React.FC<ICartProps> = ({ reject }) => {
  const { cardInfo, brandConfig } = memberMobx
  const [bankCards, setBankCards] = useState([])
  const history = CashierController.history()
  const ApplePaySession = (window as any).ApplePaySession
  const methods = [
    {
      key: 'bankCardOnline',
    },
    {
      key: 'alipay',
    },
    {
      key: 'wechatPay',
    },
    {
      key: 'applePay',
    },
  ]
  let enabledMethods = sortBy(filter(methods.map(x => ({ ...x, channel: payMobx.methodChannelMap?.[x.key] })), (x) => !!payMobx.methodChannelMap?.[x.key]), v => payMobx.channelSortMap?.[v?.channel]?.sort === undefined ? 9999 : payMobx.channelSortMap?.[v?.channel]?.sort)
  if (!ApplePaySession) {
    enabledMethods = filter(enabledMethods, x => x.key !== 'applePay')
  }
  const { payInfo, virtualOrder, rechargeInfo } = payRechargeMobx

  const handleBuy = async (targetCard: any) => {
    const orderList: any = [
      {
        type: 'memberCz',
        cnt: 1,
        productCategoryNo: 'memberCardRecharge',
        productNo: cardInfo.cardNo,
        productName: cardInfo.card.cardName,
        productScaleNo: targetCard.cardRechargeId
          ? `${targetCard.cardRechargeId}`
          : '0',
        paidFee: targetCard.money,
        originalFee: targetCard.money,
      },
    ]
    const orderParams: any = {
      storeId: brandConfig?.isSelectStoreWhenCz ? storeMobx.info.storeId : 0,
      totalFee: targetCard.money,
      details: orderList,
      eatType: 'memberCz',
      contactInfo: '',
    }
    const result: any = await virtualOrderMobx.placeOrder(orderParams)
    if (result.error) {
      let msg: string = result.message || ''
      if (result.code === 5038) {
        msg = i18n.t('mkt.member.notRechargeTip')
      }
      toastLayer.error({
        content: msg,
        duration: 1000,
      })
      await payRechargeMobx.reset()
      await discountMobx.deleteOnlyBalancePay()
      await delayTime(500)
      await discountMobx.setOnlyBalancePay(true)
      reject()
    }
    if (result && result.ordersId) {
      payRechargeMobx.setVirtualOrder(result)
      await payMobx.init({ scene: 'giftcard_buy', orderId: result.ordersId })
    }
  }

  const onClick = (item?: any) => {
    if (!item) return
    if (item.key === 'bankCardOnline') {
      const bankCardMethod = payInfo?.bankCardInfo || bankCards?.[0]
      if (!bankCardMethod) {
        const channel = payMobx.methodChannelMap[item.key]
        let tokenType: 'multi' | 'single' = 'multi'
        if (channel === 'HeartLandBankCard') {
          tokenType = (payMobx.channelAccountMap.HeartLandBankCard?.tokenType || 'single') as 'multi' | 'single'
        }
        let mutCard: IMutBankCardInfo | undefined
        if (tokenType === 'multi') {
          mutCard = payInfo?.bankCardInfo || bankCards?.[0]
        }
        item.bankCardInfo = {
          tokenType,
          creditCardNo: mutCard?.creditCardNo,
          creditCardNumber: mutCard?.creditCardNumber,
        }
      } else {
        item.bankCardInfo = bankCardMethod
      }
    } else if (item.key === 'alipay') {
      item.type = 'AlipayApp'
      item.channel = payMobx.methodChannelMap[item.key]
    } else if (item.key === 'wechatPay') {
      item.type = 'WechatPayWeb'
      item.channel = payMobx.methodChannelMap[item.key]
    }
    payRechargeMobx.setPayInfo(item)
  }

  const getReturnUrl = () => {
    const url = window.location.href
    let prefix = url.substr(0, url.indexOf('/pay'))
    if (prefix.indexOf('localhost') > -1) {
      prefix = ENV.WEBSITE
    }
    const returnUrl = prefix + `/result/${orderMobx.order.ordersId}`
    console.log('pay return url', returnUrl)
    return returnUrl
  }

  const getOpenIdData = async () => {
    const obj = JSON.parse(memberMobx.thirdAuthInfo) || {}
    const { isWeixinMiniProgram } = getPlatform()
    let openIdData: { openId: string; appId?: string }
    if (isWeixinMiniProgram) {
      openIdData = {
        openId: window.Config.openId,
        appId: window.Config.appId,
      }
    } else {
      openIdData = {
        openId: obj.gOpenId,
      }
    }
    if (!openIdData.openId) {
      openIdData = await payMobx.fetchGOpenIdAndSave()
    }
    return openIdData
  }

  const billToChannel = async (bill: any): Promise<{
    ext?: any;
    onSuccess?(): void;
    onFailed?(): void;
  }> => {
    const channelType = payMobx.methodChannelMap[bill.key]
    const bankCardInfo = bill?.bankCardInfo
    const amount = rechargeInfo.money
    let openIdData
    if (bill.key === 'wechatPay' && bill.openIdData) {
      openIdData = bill.openIdData
    } else if (['wechatPay', 'alipay'].includes(bill.key)) {
      openIdData = (await getOpenIdData()) || {}
    }
    let ext
    if (channelType === 'CardConnectBankCard') {
      bankCardMobx.setLastUsed({ tokenType: 'multi', creditCardNo: bankCardInfo.creditCardNo })
      ext = { creditCardNo: bankCardInfo.creditCardNo }
    } else if (channelType === 'HeartLandBankCard') {
      if (bankCardInfo) {
        bankCardMobx.setLastUsed({ tokenType: 'multi', creditCardNo: bankCardInfo.creditCardNo })
        ext = { creditCardNo: bankCardInfo.creditCardNo }
      } else {
        const token = await new Promise((resolve, reject) => {
          // is todo
          // setShowTokenizer(true)
          // onBankCardToken = (token) => {
          //   setShowTokenizer(false)
          //   setShowLoading(true)
          //   resolve(token)
          // }
          // onBankCardCancel = () => {
          //   setShowLoading(true)
          //   reject(new Error('CANCEL'))
          // }
        })
        ext = { authCode: token }
      }
    } else if (channelType === 'CitconAlipayWeb') {
      ext = { returnUrl: getReturnUrl() }
    } else if (channelType === 'CitconWechatPayWeb') {
      ext = { returnUrl: getReturnUrl() }
    } else if (channelType === 'CardConnectApplePay' || channelType === 'GlobalPaymentsApplePay') {
      const total: ApplePayJS.ApplePayLineItem = {
        type: 'final',
        label: 'Dining',
        amount: String(amount)
      }
      return new Promise((resolve, reject) => {
        const session = new ApplePaySession(1, {
          countryCode: 'US',
          currencyCode: 'USD',
          supportedNetworks: ['amex', 'visa', 'masterCard', 'discover'],
          merchantCapabilities: ['supports3DS'],
          total,
        })
        session.onvalidatemerchant = async function (event) {
          console.log('onvalidatemerchant', event)
          try {
            const merchantSession = await payMobx.applePayMerchantValidate(event)
            session.completeMerchantValidation(merchantSession)
          } catch (e) {
            session.abort()
            reject(e)
          }
        }
        session.onpaymentmethodselected = async function (event) {
          console.log('onpaymentmethodselected', event)
          session.completePaymentMethodSelection({ newTotal: total })
        }
        session.onpaymentauthorized = async function (event) {
          console.log('onpaymentauthorized', event)
          const { paymentData } = event.payment.token
          const { data, header, signature, version } = paymentData
          const { ephemeralPublicKey, publicKeyHash, transactionId } = header
          resolve({
            ext: {
              data,
              signature,
              version,
              ephemeralPublicKey,
              publicKeyHash,
              transactionId,
            },
            onSuccess () {
              session.completePayment((window as any).ApplePaySession.STATUS_SUCCESS)
            },
            onFailed () {
              session.abort()
            }
          })
        }
        session.oncancel = function () {
          console.log('oncancel')
          reject('cancel')
        }
        session.begin()
      })
    } else if (channelType === 'StripeApplePay') {
      return new Promise((resolve, reject) => {
        const Stripe = (window as any).Stripe
        console.log(payChannelMobx.channelAccount.StripeApplePay?.publicApiKey)
        Stripe.setPublishableKey(payChannelMobx.channelAccount.StripeApplePay?.publicApiKey)
        const paymentRequest = {
          countryCode: 'US',
          currencyCode: 'USD',
          total: {
            label: storeMobx.info.storeName,
            amount: String(amount),
          }
        }
        const session = Stripe.applePay.buildSession(paymentRequest, (result, completion) => {
          resolve({
            ext: { authCode: result.token.id },
            onSuccess () {
              completion((window as any).ApplePaySession.STATUS_SUCCESS)
            },
            onFailed () {
              completion((window as any).ApplePaySession.STATUS_FAILURE)
            }
          })
        }, reject)
        session.oncancel = function () {
          console.log('oncancel')
          reject('cancel')
        }
        session.begin()
      })
    } else if (['WechatPayWeb', 'ShengPayWechatWeb', 'ShengPayWechatWebV2'].includes(channelType)) {
      ext = openIdData
    } else if (channelType === 'AlipayWeb') {
      ext = { returnUrl: getReturnUrl() }
    } else if (['AlipayApp', 'ShengPayAlipayWeb', 'ShengPayAlipayWebV2'].includes(channelType)) {
      ext = {
        buyerId: openIdData.openId
      }
    } else if (channelType === 'PayPalWeb') {
      ext = { returnUrl: getReturnUrl() }
    }
    return ext
  }

  // 充值并支付
  const ordersBatchPay = async () => {
    if (!enabledMethods.length) {
      reject()
      return
    }
    if (isPayOrdering) {
      return
    }
    isPayOrdering = true
    try {
      let methodParam: any
      try {
        if (!cartMobx.ordersId) {
          console.log('saveOrder =>>', '下单验证排队时长 ordersBatchPay')
          /** 立即时间是否偏差 */
          const isAspaTimeExpire = await timePickMobx.checkWaitingTimeToAspaTime()
          if (isAspaTimeExpire) {
            modalLayer.closeAll()
            return
          }
          await saveOrder()
        }
        methodParam = await CheckoutPayController.preOrdersBatchPay(payInfo)
        methodParam = methodParam || payInfo
      } catch (e) {
        if (e.doc_url) {
          // 这个是stripe报的错。要等apple弹窗关闭后展示。
          setTimeout(() => {
            toastLayer.error({
              content: e.message,
              duration: 1000,
            })
          }, 2000)
        } else if (e.message) {
          toastLayer.error({
            content: e.message,
            duration: 1000,
          })
        }
        isPayOrdering = false
        return
      }
      // 充值单参数
      const rechargeChannelExt = await billToChannel(methodParam)
      await cartMobx.syncMkt()
      payPlanMobx.beginCalc()
      const rechargeData = {
        ...pick(virtualOrder, 'brandId', 'storeId', 'ordersId', 'paidFee', 'tax'),
        channels: [
          {
            amount: virtualOrder.paidFee,
            channelType: payMobx.methodChannelMap[methodParam.key],
            tip: virtualOrder.tip,
            ext: rechargeChannelExt?.ext || rechargeChannelExt,
          },
        ],
      }
      // 点餐单参数
      const ordersData = {
        ...pick(
          orderMobx.order,
          'brandId',
          'storeId',
          'ordersId',
          'paidFee',
          'tax'
        ),
        channels: [
          payPlanMobx.totalAmount > 0 ? {
            channelType: 'MemberBalance',
            amount: toDecimal(payPlanMobx.totalAmount, 2),
            tip: toDecimal(payPlanMobx.tipAmount, 2),
            ext: {
              cardId: cardInfo.cardId,
              cardNumber: cardInfo.cardNo,
              memberId: memberMobx.memberInfo.memberId,
              token: memberMobx.token,
            },
          } : {
            channelType: 'ZeroPay',
            amount: 0,
            tip: 0,
            ext: {}
          },
        ],
      }
      payPlanMobx.endCalc()
      loadingLayer.show()
      try {
        await CheckoutPayController.ordersBatchPay(methodParam, [rechargeData, ordersData])
        RestoreOrderController.clearHold()
        // 先付费下单成功清理当前购物车的缓存,订单的缓存
        cartMobx.reset(cartMobx.info.payMode, cartMobx.info.eatType)
        // discountMobx.clearDiscountAction()
        memberMobx.refreshMemeberBenefits()
        // 支付成功后，重置时间
        timePickMobx.reset()
        payRechargeMobx.reset()
        CashierController.toResult(ordersData.ordersId)
        isPayOrdering = false
        loadingLayer.hide()
      } catch (e) {
        CashierController.handleStartPayError(e)
        isPayOrdering = false
        loadingLayer.hide()
        payRechargeMobx.reset()
        history.replace({
          pathname: '/payfail',
          state: {
            reason: e.message,
          },
        })
        trace.traceLog({
          name: '支付: => 支付异常',
          message: { error: e, ordersId: ordersData.ordersId }
        })
      }
    } catch (e) {
      if (e.message !== 'CANCEL') {
        console.log('ordersBatchPay error: =>', e.message)
        toastLayer.error({
          content: e.message,
          duration: 1000,
        })
      }
    } finally {
      isPayOrdering = false
      loadingLayer.hide()
    }
  }

  useEffect(() => {
    if (payRechargeMobx.isReset(rechargeInfo)) {
      handleBuy(rechargeInfo)
      payRechargeMobx.setCacheRechargeInfo(rechargeInfo)
    }
  }, [rechargeInfo])

  useEffect(() => {
    onClick(payInfo || enabledMethods[0])
  }, [payMobx.methodChannelMap, payInfo])

  useEffect(() => {
    const memberBankCards = map(payMobx.bankCards, ({ id, number }) => {
      return {
        tokenType: 'multi',
        creditCardNo: id,
        creditCardNumber: number,
      }
    })
    setBankCards(memberBankCards)
  }, [payMobx.bankCards])

  return (
    <IonModal
      isOpen
      cssClass='pay-and-recharge-modal height620-modal'
      onDidDismiss={reject}
    >
      <IonHeader>
        <div className='pay-and-recharge-header'>
          <span>{i18n.t('pay.rechargeAndPay')}</span>
          <i className='iconfont icon-close' data-cypid='payAndRechargeClose' onClick={reject} />
        </div>
      </IonHeader>
      <IonContent className='pay-and-recharge-content'>
        <div className='pay-amount' dangerouslySetInnerHTML={{ __html: format.toPriceLittleCurrency(rechargeInfo?.money || 0) }}></div>
        <div className='pay-and-recharge-pay'>
          <div className='external-pay'>
            {
              enabledMethods.map((x) => {
                if (x.key === 'bankCardOnline') {
                  return (
                    <BankCardSimpleItem
                      payRechargeFlag
                      bankCards={bankCards}
                      selected={payInfo?.key === 'bankCardOnline'}
                      key={x.key}
                      amount={rechargeInfo?.money}
                      onChange={(data) => {
                        if (!data) {
                          onClick(x)
                        } else {
                          payRechargeMobx.setPayInfo({ bankCardInfo: data, ...x })
                        }
                      }}
                      card={payRechargeMobx.payInfo?.bankCardInfo}
                    />
                  )
                } else if (x.key === 'applePay' && !!ApplePaySession) {
                  const method = {
                    amount: rechargeInfo?.money
                  }
                  return (
                    <ApplePayMethodItem
                      selected={payInfo?.key === 'applePay'}
                      key={x.key}
                      method={method}
                      onChange={() => onClick(x)}
                    />
                  )
                } else if (x.key === 'alipay') {
                  return (
                    <AlipaySimpleItem
                      selected={payInfo?.key === 'alipay'}
                      key={x.key}
                      amount={rechargeInfo?.money}
                      onChange={() => onClick(x)}
                    />
                  )
                } else if (x.key === 'wechatPay') {
                  return (
                    <WechatSimpleItem
                      selected={payInfo?.key === 'wechatPay'}
                      key={x.key}
                      amount={rechargeInfo?.money}
                      onChange={() => onClick(x)}
                    />
                  )
                }
              })
            }
          </div>
        </div>
      </IonContent>
      <IonFooter className='pay-and-recharge-footer'>
        <div className={`footer-pay${enabledMethods.length > 0 ? '' : ' disabled'}`} data-cypid='payAndRechargeConfirm' onClick={ordersBatchPay}>
          <div>{i18n.t('pay.rechargeAndPay')}</div>
        </div>
      </IonFooter>
    </IonModal>
  )
}

export default observer(PayAndRecharge)
